import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";

/* ──────────────────────────────────────────────────────────────────────────
 * Clickable NAP Code (used in completed Job Order / Migration detail modals
 * and table views). Opens a dropdown to view the NAP location on the NI Map,
 * Migration Map, or NAP Locator. The dropdown is rendered via a portal so it
 * is never clipped by scrollable/overflow-hidden table containers.
 * ────────────────────────────────────────────────────────────────────────── */

const DESTINATIONS = [
  { key: "ni", label: "View on NI Map Page", route: "/ni/dashboard", perm: "page.ni_dashboard" },
  { key: "mig", label: "View on Mig Map Page", route: "/migration/dispatch-dashboard", perm: "page.migration_dashboard" },
  { key: "nap", label: "View on NAP Locator", route: "/nap-locator", perm: "page.nap_locator" },
];

export default function NapCodeMapLinks({ napCode }) {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 224 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);

  const options = DESTINATIONS.filter((d) => hasPerm(d.perm));

  // Close on outside click — but NOT when clicking inside the portal dropdown
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 224),
      });
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  // No code, or no accessible destination → plain read-only text.
  if (!napCode || options.length === 0) {
    return (
      <p className="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 border">{napCode || "—"}</p>
    );
  }

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 224),
      });
    }
    setOpen((o) => !o);
  };

  const go = async (route) => {
    setOpen(false);
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get("/nap-locator", {
        params: { search: napCode, page: 1, page_size: 20, sort_by: "nap_code", sort_dir: "asc" },
      });
      const norm = (s) => String(s || "").trim().toUpperCase().replace(/\s+/g, " ");
      const match = (Array.isArray(data) ? data : []).find((o) => norm(o.nap_code) === norm(napCode));
      if (!match) { setError("NAP code not found in NAP Locator."); setBusy(false); return; }
      if (match.latitude == null || match.longitude == null) { setError("This NAP has no coordinates set."); setBusy(false); return; }
      navigate(route, {
        state: { focusNap: { nap_code: match.nap_code, latitude: match.latitude, longitude: match.longitude } },
      });
    } catch {
      setError("Failed to look up NAP location.");
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        disabled={busy}
        title="View this NAP on a map"
        className="w-full text-left text-sm font-medium text-blue-700 bg-gray-50 rounded px-3 py-2 border hover:bg-blue-50 hover:border-blue-300 inline-flex items-center justify-between gap-2 disabled:opacity-60"
      >
        <span className="underline decoration-dotted">{busy ? "Locating…" : napCode}</span>
        <svg className="w-4 h-4 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: dropdownPos.top + 4,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 10060,
          }}
          className="bg-white border rounded-lg shadow-lg overflow-hidden"
        >
          {options.map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => go(d.route)}
              className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-blue-50"
            >
              {d.label}
            </button>
          ))}
        </div>,
        document.body
      )}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

